package app.smartcoding.realrate.presentation.components

import androidx.compose.animation.animateContentSize
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells.Adaptive
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridItemSpan
import androidx.compose.foundation.lazy.staggeredgrid.items
import androidx.compose.foundation.lazy.staggeredgrid.rememberLazyStaggeredGridState
import androidx.compose.foundation.shape.CornerSize
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExperimentalMaterial3ExpressiveApi
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.TopAppBarScrollBehavior
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import app.smartcoding.realrate.domain.model.adBannerData
import app.smartcoding.realrate.domain.tools.toDoubleOrZero
import app.smartcoding.realrate.localization.LocalStrings
import app.smartcoding.realrate.platform.AdBanner
import app.smartcoding.realrate.presentation.AuthViewModel
import app.smartcoding.realrate.presentation.CreditViewModel
import app.smartcoding.realrate.presentation.SettingsViewModel

@OptIn(
    ExperimentalMaterial3Api::class,
    ExperimentalLayoutApi::class,
    ExperimentalMaterial3ExpressiveApi::class
)
@Composable
fun CreditAnalyzer(
    creditViewModel: CreditViewModel,
    authViewModel: AuthViewModel,
    settingsViewModel: SettingsViewModel
) {
    val itemMinWidth = 320.dp
    var showInfo: Boolean by remember { mutableStateOf(false) }
    var showAccount: Boolean by remember { mutableStateOf(false) }
    val lazyStaggeredGridState = rememberLazyStaggeredGridState()
    val scrollBehavior: TopAppBarScrollBehavior = TopAppBarDefaults.enterAlwaysScrollBehavior()
    var isSignIn by remember { mutableStateOf(false) }
    var isSignOut by remember { mutableStateOf(false) }
    val showAds = settingsViewModel.showAds

    println("showAds = $showAds")

    if (isSignIn) {
        authViewModel.SignIn(
            onSuccess = { isSignIn = false },
            onFailure = { isSignIn = false }
        )
    }
    if (isSignOut) {
        authViewModel.SignOut(
            onSuccess = { isSignOut = false },
            onFailure = { isSignOut = false }
        )
    }

    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val isMobile = maxWidth < 600.dp

        Scaffold(
            modifier = Modifier.nestedScroll(scrollBehavior.nestedScrollConnection),
            containerColor = MaterialTheme.colorScheme.surfaceContainer,
            topBar = {
                TopBar(
                    scrollBehavior = scrollBehavior,
                    onClickAccount = { showAccount = true },
                    authUser = authViewModel.authUser
                )
            }
        ) { paddingValues ->
            val inputs = creditViewModel.inputs
            val result = creditViewModel.result
            val error = creditViewModel.error
            val showStickyHeader by remember(result, lazyStaggeredGridState) {
                derivedStateOf {
                    val headerIndex = 5
                    val layoutInfo = lazyStaggeredGridState.layoutInfo
                    val visibleItems = layoutInfo.visibleItemsInfo
                    val headerItem = visibleItems.find { it.index == headerIndex }

                    if (headerItem != null) {
                        headerItem.offset.y <= 0
                    } else {
                        lazyStaggeredGridState.firstVisibleItemIndex > headerIndex
                    }
                }
            }
            val spacing = 16.dp
            val horizontalPadding = 24.dp

            val columns by remember(maxWidth) {
                derivedStateOf {
                    val availableWidth = maxWidth - horizontalPadding
                    ((availableWidth + spacing) / (itemMinWidth + spacing)).toInt().coerceAtLeast(1)
                }
            }

            LazyVerticalStaggeredGrid(
                columns = Adaptive(itemMinWidth),
                modifier = Modifier
                    .padding(paddingValues = paddingValues)
                    .fillMaxSize(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalItemSpacing = 8.dp,
                state = lazyStaggeredGridState,
                contentPadding = PaddingValues(16.dp),
            ) {

                result?.result?.let { result ->
                    item(key = "result") { Result1(result) }
                }

                inputs?.let {
                    item(key = "inputs") {
                        InputFields(
                            inputs = it,
                            onLoanFieldsChanged = creditViewModel::onLoanFieldsChanged,
                            onToggleAmountDetails = creditViewModel::onToggleShowDetailedLoanAmountInput,
                            onToggleLockedTaxRate = creditViewModel::onToggleLockedTaxRate,
                            onMonthsFieldChanged = creditViewModel::onMonthsFieldChanged,
                            onToggleMountsDetails = creditViewModel::onToggleShowDetailedMountsInput,
                            onPaymentFieldsChanged = creditViewModel::onPaymentFieldsChanged,
                            onTogglePaymentDetails = creditViewModel::onToggleShowDetailedMountPaymentInput
                        )
                    }
                }

                error?.let {
                    item(key = "error") {
                        Error(error = error)
                    }
                }

                if (showAds) {
                    item(key = "ad_banner_key") {
                        adBannerData.AdBanner(
                            modifier = Modifier
                                .clip(MaterialTheme.shapes.large)
                                .background(MaterialTheme.colorScheme.surfaceContainerLow)
                                .fillMaxWidth()
                                .animateContentSize()
                        )
                    }
                }

                result
                    ?.takeIf { it.result.schedule.isNotEmpty() && error == null }
                    ?.let { (inputs, result) ->
                        item {
                            Result2(
                                principal = inputs.loanReceivedAmount.text.toDoubleOrZero(),
                                monthsValue = inputs.termsMonthsTotal.text.toIntOrNull() ?: 0,
                                paymentValue = inputs.paymentTotal.text.toDoubleOrZero()
                            )
                        }

                        item {
                            Result3(schedule = result.schedule)
                        }

                        item(
                            span = StaggeredGridItemSpan.FullLine
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(36.dp)
                                    .clip(
                                        MaterialTheme.shapes.large.copy(
                                            bottomEnd = CornerSize(0.dp),
                                            bottomStart = CornerSize(0.dp)
                                        )
                                    )
                                    .background(
                                        color = MaterialTheme.colorScheme.surfaceContainerLow,
                                    )
                            ) {
                                ScheduleTableHeader(columns = columns)
                            }
                        }

                        items(
                            items = result.schedule,
                            key = { row -> row.month },
                            contentType = { "ScheduleTableRow" }
                        ) { row ->
                            ScheduleTableRow(row)
                        }

                    }
            }
            if (showStickyHeader) {
                val shape = MaterialTheme.shapes.large.copy(
                    bottomEnd = CornerSize(0.dp),
                    bottomStart = CornerSize(0.dp)
                )
                Box(
                    modifier = Modifier
                        .padding(paddingValues = paddingValues)
                        .padding(horizontal = 16.dp)
                        .fillMaxWidth()
                        .height(36.dp)
                        .clip(shape)
                        .background(color = MaterialTheme.colorScheme.surfaceContainerLowest)
                ) {
                    ScheduleTableHeader(columns = columns)
                }
            }
            if (showInfo) {
                AlertDialog(
                    onDismissRequest = { showInfo = false },
                    confirmButton = {
                        TextButton(
                            onClick = { showInfo = false }
                        ) {
                            Text(LocalStrings.current.agree)
                        }
                    },
                    title = { Text(LocalStrings.current.information) },
                    text = { Text(LocalStrings.current.description) },
                    icon = { Icon(Icons.Default.Info, contentDescription = null) },
                    shape = MaterialTheme.shapes.extraLarge,
                    containerColor = MaterialTheme.colorScheme.surfaceContainerHigh
                )
            }
            if (showAccount) {
                Dialog(
                    onDismissRequest = {
                        showAccount = false
                    },
                    properties = DialogProperties(
                        usePlatformDefaultWidth = !isMobile
                    )
                ) {
                    val modifier = if (isMobile) {
                        Modifier.fillMaxSize()
                    } else {
                        Modifier
                            .padding(vertical = 32.dp)
                            .widthIn(max = 400.dp)
                    }
                    Card(
                        modifier = modifier,
                        shape = if (isMobile) MaterialTheme.shapes.extraLarge else MaterialTheme.shapes.large
                    ) {
                        AccountScreen(
                            currentUser = authViewModel.authUser,
                            onSignIn = { isSignIn = true },
                            onSignOut = { isSignOut = true },
                            onDismiss = { showAccount = false },
                            currentTheme = settingsViewModel.settings.theme,
                            onThemeSelected = settingsViewModel::onThemeSelected,
                            currentLanguageCode = settingsViewModel.settings.languageCode,
                            onLanguageCodeSelected = settingsViewModel::onLanguageCodeSelected,
                        )
                    }
                }
            }
        }
    }
}